import { createBlock } from './blockUtils';
import { sanitizeVideoBlockProps } from './videoFiles';

/** Max size for doc images (bytes). */
export const DOC_IMAGE_MAX_BYTES = 20 * 1024 * 1024;

export const DOC_IMAGE_ALLOWED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
];

/**
 * @param {File} file
 * @returns {{ ok: true } | { ok: false, error: string }}
 */
export const validateDocImageFile = (file) => {
  if (!file) return { ok: false, error: 'No file selected' };
  if (!DOC_IMAGE_ALLOWED_TYPES.includes(file.type)) {
    return { ok: false, error: 'Use PNG, JPEG, GIF, or WebP' };
  }
  if (file.size > DOC_IMAGE_MAX_BYTES) {
    const mb = Math.round(file.size / (1024 * 1024));
    return { ok: false, error: `Image too large (${mb}MB). Maximum is 20MB` };
  }
  return { ok: true };
};

/**
 * Build an image block with a temporary local preview; caller should kick off
 * upload via startDocImageUpload(block.id, file).
 * @param {File} file
 */
export const createLocalImageBlock = (file) => {
  const check = validateDocImageFile(file);
  if (!check.ok) {
    return createBlock('image', '', {
      uploadStatus: 'error',
      errorMessage: check.error,
      fileName: file?.name || '',
    });
  }
  return createBlock('image', '', {
    localPreviewUrl: URL.createObjectURL(file),
    fileName: file.name,
    mimeType: file.type,
    size: file.size,
    uploadStatus: 'local',
  });
};

/**
 * Strip blob URLs and unfinished upload fields before persisting a doc body.
 * Keeps durable publicUrl / metadata so refresh still shows uploaded images.
 */
export const sanitizeBlocksForPersist = (blocks) => {
  if (!Array.isArray(blocks)) return blocks;
  return blocks.map((b) => {
    const children = b.children?.length
      ? sanitizeBlocksForPersist(b.children)
      : b.children;
    if (b.type === 'video') {
      return { ...b, props: sanitizeVideoBlockProps(b.props), children };
    }
    if (b.type !== 'image') {
      return children !== b.children ? { ...b, children } : b;
    }
    const props = { ...(b.props || {}) };
    delete props.localPreviewUrl;
    if (props.publicUrl) {
      props.uploadStatus = 'ready';
      delete props.errorMessage;
    } else if (props.uploadStatus === 'uploading' || props.uploadStatus === 'local') {
      // Mid-upload save: keep the slot but no blob URL (refresh would lose it anyway)
      props.uploadStatus = 'error';
      props.errorMessage = props.errorMessage || 'Image upload was interrupted';
    }
    return { ...b, props, children };
  });
};

/**
 * Open a hidden file picker for a single image.
 * @returns {Promise<File|null>}
 */
export const pickDocImageFile = () =>
  new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = DOC_IMAGE_ALLOWED_TYPES.join(',');
    input.style.display = 'none';
    const cleanup = () => {
      input.remove();
    };
    input.addEventListener('change', () => {
      const file = input.files?.[0] || null;
      cleanup();
      resolve(file);
    });
    // Cancel: browsers don't always fire change — resolve null on window focus
    const onFocus = () => {
      setTimeout(() => {
        if (!input.files?.length) {
          cleanup();
          resolve(null);
        }
      }, 300);
      window.removeEventListener('focus', onFocus);
    };
    window.addEventListener('focus', onFocus);
    document.body.appendChild(input);
    input.click();
  });

/**
 * Collect image File objects from a paste or drop dataTransfer.
 * @param {DataTransfer|null|undefined} data
 * @returns {File[]}
 */
export const imageFilesFromDataTransfer = (data) => {
  if (!data) return [];
  const fromFiles = [...(data.files || [])].filter((f) =>
    DOC_IMAGE_ALLOWED_TYPES.includes(f.type)
  );
  if (fromFiles.length) return fromFiles;

  const fromItems = [];
  for (const item of data.items || []) {
    if (item.kind === 'file' && DOC_IMAGE_ALLOWED_TYPES.includes(item.type)) {
      const file = item.getAsFile();
      if (file) fromItems.push(file);
    }
  }
  return fromItems;
};
